/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { HDMPriceDisplay } from '@/components/blocks/HDMPriceDisplay';
import { PriceVariant } from '@/components/blocks/HDMPriceDisplay/types';
import { priceSX } from '@/components/blocks/ProductDetails/styles/price';
import { PromoIcons } from '@/components/blocks/PromoIcons';
import { USAGE_DISPLAY, USAGE_OFFER } from '@/data/constants/catalog';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useContext } from 'react';

export const ProductDetailsPrice: FC<{
	isStickyComponent?: boolean;
	variant?: PriceVariant;
}> = ({ isStickyComponent = false, variant }) => {
	const localization = useLocalization('PriceDisplay');
	const theme = useTheme();
	const { selection, product: parentProduct } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;
	const product = selection?.sku ? selection?.sku : parentProduct;

	const isKitOrBundle = isKitOrBundleType[product?.type as string];

	const kitSavings = isKitOrBundle
		? product?.components?.reduce((acc, item) => {
				if (!item.quantity) return acc;

				const quantity = Number(item.quantity);
				const displayPriceStr = item.price.find((i) => i.usage === USAGE_DISPLAY)?.value;
				const offerPriceStr = item.price.find((i) => i.usage === USAGE_OFFER)?.value;

				const displayPrice = displayPriceStr ? Number(displayPriceStr) : undefined;
				const offerPrice = offerPriceStr ? Number(offerPriceStr) : undefined;

				if (displayPrice === undefined && offerPrice === undefined) return acc;

				const price = (displayPrice !== undefined ? displayPrice : offerPrice) ?? 0;

				return acc + price * quantity;
		  }, 0)
		: undefined;

	const productPrice = isKitOrBundle
		? { ...product?.productPrice, list: kitSavings }
		: product?.productPrice;

	return (
		<Stack
			direction="row"
			spacing={0.375}
			p={'10px'}
			alignItems={'start'}
			sx={priceSX(theme, isStickyComponent).container}
		>
			{isStickyComponent ? null : <PromoIcons product={product!} />}
			{product?.productPrice.offer ? (
				isStickyComponent ? (
					<Box id="stickyProductPrice">
						<HDMPriceDisplay price={productPrice ?? {}} variant={variant} />
					</Box>
				) : (
					<HDMPriceDisplay price={productPrice ?? {}} variant={variant} />
				)
			) : (
				<Typography variant="body1" align="left">
					{localization.Labels.Pending.t()}
				</Typography>
			)}
		</Stack>
	);
};
